import { Button } from "@/components/ui/button";
import { ArrowRight, ArrowDown, Layers, Monitor, Smartphone, Sparkles, Activity } from "lucide-react";
import { motion } from "framer-motion";
import { ScrollReveal, fadeInLeft, fadeInRight, StaggerContainer, StaggerItem } from "@/components/animations/ScrollAnimations";
import chargingSite from "@/assets/charging_site.png";

const products = [
  { id: "platform", icon: Layers, name: "SaaS Platform", description: "Enterprise management & analytics", color: "primary" },
  { id: "portal", icon: Monitor, name: "Web Portal", description: "White-label web experience", color: "accent" },
  { id: "apps", icon: Smartphone, name: "Mobile Apps", description: "iOS & Android driver apps", color: "primary" },
];

export function ProductHeroNew() {
  return (
    <section className="relative min-h-screen flex flex-col pt-20 overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-subtle" />
      
      {/* Ambient orbs */}
      <motion.div 
        className="absolute top-1/4 right-[5%] w-[600px] h-[600px] rounded-full pointer-events-none ambient-orb"
        style={{ 
          background: 'radial-gradient(circle, hsl(var(--primary) / 0.12) 0%, transparent 70%)',
          filter: 'blur(80px)'
        }}
      />
      <motion.div 
        className="absolute bottom-1/3 left-[5%] w-[500px] h-[500px] rounded-full pointer-events-none"
        style={{ 
          background: 'radial-gradient(circle, hsl(var(--accent) / 0.08) 0%, transparent 70%)',
          filter: 'blur(60px)'
        }}
        animate={{ scale: [1, 1.15, 1], opacity: [0.3, 0.5, 0.3] }}
        transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
      />

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10 flex-1 flex flex-col">
        {/* Main Content */}
        <div className="flex-1 flex items-center py-12">
          <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 w-full items-center">
            {/* Left - Main Content */}
            <div className="lg:col-span-6">
              <ScrollReveal variants={fadeInLeft} className="relative">
                <motion.div 
                  className="inline-flex items-center gap-2 badge-primary mb-6"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                >
                  <Sparkles className="h-3.5 w-3.5" />
                  Product Suite
                </motion.div>
                
                <h1 className="text-5xl md:text-6xl lg:text-7xl font-extrabold leading-[0.9] tracking-tight mb-8">
                  <span className="text-foreground block">One Platform,</span>
                  <span className="text-gradient block py-2">Infinite</span>
                  <span className="text-foreground block">Possibilities</span>
                </h1>
                
                <p className="text-lg md:text-xl text-muted-foreground max-w-lg mb-10 leading-relaxed font-light">
                  From enterprise SaaS to white-label apps—everything you need to deploy, manage, and scale your EV charging infrastructure.
                </p>

                <motion.div 
                  className="flex flex-wrap gap-4"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5 }}
                >
                  <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                    <a href="https://outlook.office365.com/book/EvGatewayPortalDemos@evgateway.com/" target="_blank" rel="noopener noreferrer">
                      <Button variant="hero" size="lg" className="group text-base px-8 py-6 shadow-lg shadow-primary/20">
                        Request Demo
                        <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
                      </Button>
                    </a>
                  </motion.div>
                  <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                    <Button 
                      variant="outline" 
                      size="lg" 
                      className="border-foreground/30 text-foreground hover:bg-foreground/10 hover:border-foreground/50 transition-all text-base px-8 py-6"
                    >
                      Watch Demo
                    </Button>
                  </motion.div>
                </motion.div>
              </ScrollReveal>
            </div>

            {/* Right - Product Cards */}
            <ScrollReveal variants={fadeInRight} className="lg:col-span-6">
              <StaggerContainer className="grid gap-4" staggerDelay={0.1}>
                {products.map((product) => (
                  <StaggerItem key={product.id}>
                    <motion.a
                      href={`#${product.id}`}
                      whileHover={{ scale: 1.02, x: 8 }}
                      transition={{ duration: 0.3, ease: "easeOut" }}
                      className={`group flex items-center gap-6 p-6 glass-card hover:border-${product.color}/40 transition-all duration-300`}
                    >
                      <motion.div 
                        className={`w-16 h-16 rounded-2xl bg-${product.color}/10 border border-${product.color}/20 flex items-center justify-center`}
                        whileHover={{ scale: 1.1, rotate: 5 }}
                      >
                        <product.icon className={`h-8 w-8 text-${product.color}`} />
                      </motion.div>
                      <div className="flex-1">
                        <h3 className="text-xl font-bold mb-1 tracking-tight text-white">{product.name}</h3>
                        <p className="text-sm text-white/60 font-light">{product.description}</p>
                      </div>
                      <div className="w-10 h-10 rounded-full border border-white/20 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                        <ArrowRight className="h-4 w-4 text-white/60 group-hover:text-white" />
                      </div>
                    </motion.a>
                  </StaggerItem>
                ))}
              </StaggerContainer>

              {/* Stats */}
              <motion.div 
                className="grid grid-cols-3 gap-4 mt-6"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.8 }}
              >
                {[
                  { value: "99.9%", label: "Uptime", icon: Activity },
                  { value: "OCPP", label: "Compliant" },
                  { value: "24/7", label: "Support" },
                ].map((stat, idx) => (
                  <motion.div 
                    key={idx} 
                    className="text-center p-5 glass-card"
                    whileHover={{ scale: 1.03 }}
                  >
                    <div className="text-xl font-bold text-primary tracking-tight">{stat.value}</div>
                    <div className="text-xs text-white/60 font-medium">{stat.label}</div>
                  </motion.div>
                ))}
              </motion.div>
            </ScrollReveal>
          </div>
        </div>

        {/* Charging Site Image */}
        <motion.div 
          className="py-12"
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1, duration: 0.8 }}
        >
          <div className="glass-card p-4 md:p-6 overflow-hidden rounded-3xl">
            <img 
              src={chargingSite} 
              alt="EVGateway branded EV charging stations at a parking lot" 
              className="w-full object-cover rounded-2xl max-h-[400px]"
            />
          </div>
        </motion.div>

        {/* Scroll Indicator */}
        <motion.div 
          className="flex justify-center pb-8"
          animate={{ y: [0, 6, 0] }}
          transition={{ duration: 2, repeat: Infinity }}
        >
          <a href="#platform" className="flex items-center gap-3 text-muted-foreground hover:text-primary transition-colors group">
            <span className="text-xs uppercase tracking-widest font-medium">Explore Products</span>
            <div className="w-8 h-8 rounded-full border border-border/50 flex items-center justify-center group-hover:border-primary/50 transition-colors">
              <ArrowDown className="h-4 w-4" />
            </div>
          </a>
        </motion.div>
      </div>
    </section>
  );
}